import React, { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Phone, Printer, Mail, Globe, Facebook, Instagram, Linkedin, Twitter, Youtube, Music2, Activity, Lock } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({
    company: user?.company || "",
    phone: user?.phone || "",
    fax: user?.fax || "",
    contact_email: user?.contact_email || "",
    bio: user?.bio || "",
    license_number: user?.license_number || "",
    cities: (user?.cities || []).join(", "),
    proof_of_funds_url: user?.proof_of_funds_url || "",
    website_url: user?.website_url || "",
    facebook_url: user?.facebook_url || "",
    instagram_url: user?.instagram_url || "",
    linkedin_url: user?.linkedin_url || "",
    twitter_url: user?.twitter_url || "",
    youtube_url: user?.youtube_url || "",
    tiktok_url: user?.tiktok_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((x) => ({ ...x, proof_of_funds_url: data.url }));
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        company: f.company || null,
        phone: f.phone || null,
        fax: f.fax || null,
        contact_email: f.contact_email || null,
        bio: f.bio || null,
        license_number: f.license_number || null,
        cities: f.cities ? f.cities.split(",").map(s => s.trim()).filter(Boolean) : [],
        proof_of_funds_url: f.proof_of_funds_url || null,
        website_url: f.website_url || null,
        facebook_url: f.facebook_url || null,
        instagram_url: f.instagram_url || null,
        linkedin_url: f.linkedin_url || null,
        twitter_url: f.twitter_url || null,
        youtube_url: f.youtube_url || null,
        tiktok_url: f.tiktok_url || null,
      };
      const { data } = await api.put("/auth/profile", payload);
      setUser(data);
      toast.success("Profile saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <DashboardLayout>
      <p className="eyebrow">Account</p>
      <h1 className="font-display text-3xl font-semibold mt-2">Profile</h1>
      <p className="text-sm text-[var(--adn-text-soft)] mt-1">Signed in as {user?.email}</p>

      <section className="mt-8 max-w-3xl">
        <h2 className="font-display text-lg font-semibold mb-4">Business details</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Field k="Company"><input className="p-input" value={f.company} onChange={(e)=>setF({...f, company: e.target.value})} data-testid="profile-company" /></Field>
          <Field k="License #"><input className="p-input" value={f.license_number} onChange={(e)=>setF({...f, license_number: e.target.value})} /></Field>
          <Field k="Cities (comma separated)" full><input className="p-input" value={f.cities} onChange={(e)=>setF({...f, cities: e.target.value})} placeholder="Dallas, Fort Worth" /></Field>
          <Field k="Bio" full><textarea className="p-input min-h-[100px]" value={f.bio} onChange={(e)=>setF({...f, bio: e.target.value})} /></Field>
        </div>
      </section>

      <section className="mt-10 max-w-3xl">
        <h2 className="font-display text-lg font-semibold mb-4">Contact</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Field k="Telephone" icon={Phone}><input className="p-input pl-9" value={f.phone} onChange={(e)=>setF({...f, phone: e.target.value})} placeholder="(555) 555-0100" data-testid="profile-phone" /></Field>
          <Field k="Fax" icon={Printer}><input className="p-input pl-9" value={f.fax} onChange={(e)=>setF({...f, fax: e.target.value})} placeholder="(555) 555-0101" data-testid="profile-fax" /></Field>
          <Field k="Public contact email" icon={Mail}><input className="p-input pl-9" value={f.contact_email} onChange={(e)=>setF({...f, contact_email: e.target.value})} placeholder="you@company.com" data-testid="profile-contact-email" /></Field>
          <Field k="Website" icon={Globe}><input className="p-input pl-9" value={f.website_url} onChange={(e)=>setF({...f, website_url: e.target.value})} placeholder="https://…" /></Field>
        </div>
      </section>

      <section className="mt-10 max-w-3xl">
        <h2 className="font-display text-lg font-semibold mb-4">Social media</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Field k="Facebook" icon={Facebook}><input className="p-input pl-9" value={f.facebook_url} onChange={(e)=>setF({...f, facebook_url: e.target.value})} placeholder="https://facebook.com/…" data-testid="profile-facebook" /></Field>
          <Field k="Instagram" icon={Instagram}><input className="p-input pl-9" value={f.instagram_url} onChange={(e)=>setF({...f, instagram_url: e.target.value})} placeholder="https://instagram.com/…" data-testid="profile-instagram" /></Field>
          <Field k="LinkedIn" icon={Linkedin}><input className="p-input pl-9" value={f.linkedin_url} onChange={(e)=>setF({...f, linkedin_url: e.target.value})} placeholder="https://linkedin.com/in/…" data-testid="profile-linkedin" /></Field>
          <Field k="X / Twitter" icon={Twitter}><input className="p-input pl-9" value={f.twitter_url} onChange={(e)=>setF({...f, twitter_url: e.target.value})} placeholder="https://x.com/…" data-testid="profile-twitter" /></Field>
          <Field k="YouTube" icon={Youtube}><input className="p-input pl-9" value={f.youtube_url} onChange={(e)=>setF({...f, youtube_url: e.target.value})} placeholder="https://youtube.com/@…" data-testid="profile-youtube" /></Field>
          <Field k="TikTok" icon={Music2}><input className="p-input pl-9" value={f.tiktok_url} onChange={(e)=>setF({...f, tiktok_url: e.target.value})} placeholder="https://tiktok.com/@…" data-testid="profile-tiktok" /></Field>
        </div>
      </section>

      <section className="mt-10 max-w-3xl">
        <h2 className="font-display text-lg font-semibold mb-4">Verification</h2>
        <Field k="Proof of funds (PDF or image)" full>
          <label className="flex items-center gap-3 border border-dashed border-[var(--adn-line)] bg-white px-4 py-3 cursor-pointer">
            <Upload size={16} />
            <span className="text-sm">{uploading ? "Uploading…" : (f.proof_of_funds_url ? "Uploaded ✓ (click to replace)" : "Upload PDF or image")}</span>
            <input type="file" accept=".pdf,image/*" className="hidden" onChange={onUpload} data-testid="upload-pof" />
          </label>
        </Field>
      </section>

      <ActivitySection />

      <ChangePasswordSection />

      <div className="mt-10 max-w-3xl">
        <button data-testid="save-profile" onClick={save} disabled={saving} className="btn-gold px-6 py-3 rounded text-sm">
          {saving ? "Saving…" : "Save Profile"}
        </button>
      </div>
      <style>{`
        .p-input { width: 100%; background: white; border: 1px solid #CBD5E1; padding: 10px 12px; font-size: 14px; }
        .p-input:focus { outline: none; border-color: var(--adn-navy); }
      `}</style>
    </DashboardLayout>
  );
}
function Field({ k, children, full, icon: Icon }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs font-semibold text-[var(--adn-text-soft)] mb-1.5 block uppercase tracking-wider">{k}</span>
      <div className="relative">
        {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--adn-text-muted)] pointer-events-none" />}
        {children}
      </div>
    </label>
  );
}

function ActivitySection() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    (async () => { try { const { data } = await api.get("/auth/my-activity"); setData(data); } catch {} })();
  }, []);

  const sessions = (data?.activity || []).filter((a) =>
    ["auth.login", "auth.google_login", "auth.register", "auth.login_failed", "auth.logout"].includes(a.action)
  );

  return (
    <section className="mt-10 max-w-3xl">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Activity size={18} className="text-[var(--adn-gold)]" /> Sign-in & activity</h2>
        <button onClick={() => setOpen((o) => !o)} className="text-xs underline text-[var(--adn-text-soft)] hover:text-[var(--adn-navy)]" data-testid="toggle-activity-full">
          {open ? "Hide all activity" : "Show all activity"}
        </button>
      </div>

      <div className="bg-white border border-[var(--adn-line)] p-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Mini k="Last IP" v={data?.last_ip || "—"} mono />
        <Mini k="Last seen" v={data?.last_seen_at ? fmtDate(data.last_seen_at) : "—"} />
        <Mini k="Last login" v={data?.last_login ? fmtDate(data.last_login) : "—"} />
        <Mini k="Sign-ins logged" v={sessions.length} />
        {data?.last_user_agent && (
          <Mini k="Device / browser" v={data.last_user_agent} full small />
        )}
      </div>

      <div className="bg-white border border-[var(--adn-line)] mt-4">
        <div className="px-4 py-3 border-b border-[var(--adn-line)] flex items-center justify-between">
          <p className="eyebrow">Recent sign-in attempts</p>
          <p className="text-[10px] text-[var(--adn-text-muted)] tracking-wider uppercase">Yours only — not shared</p>
        </div>
        {sessions.length === 0 ? (
          <p className="p-5 text-sm text-[var(--adn-text-soft)]">No sign-in history yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--adn-cream)] text-left text-[10px] uppercase tracking-[0.12em] text-[var(--adn-text-muted)]">
                <tr><th className="px-3 py-2.5">When</th><th className="px-3 py-2.5">Event</th><th className="px-3 py-2.5">IP</th></tr>
              </thead>
              <tbody className="divide-y divide-[var(--adn-line)]">
                {sessions.slice(0, 25).map((s) => (
                  <tr key={s.log_id} className={s.action === "auth.login_failed" ? "bg-rose-50/40" : ""}>
                    <td className="px-3 py-2 text-[var(--adn-text-soft)] text-xs whitespace-nowrap">{fmtDate(s.at)}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {s.action === "auth.login" && <span className="text-[var(--adn-emerald)] font-medium">Signed in (pw)</span>}
                      {s.action === "auth.google_login" && <span className="text-[var(--adn-emerald)] font-medium">Signed in (Google)</span>}
                      {s.action === "auth.register" && <span className="font-medium">Account created</span>}
                      {s.action === "auth.logout" && <span className="text-[var(--adn-text-soft)]">Signed out</span>}
                      {s.action === "auth.login_failed" && <span className="text-rose-700 font-medium">Failed attempt</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{s.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && (
        <div className="bg-white border border-[var(--adn-line)] mt-4">
          <div className="px-4 py-3 border-b border-[var(--adn-line)]">
            <p className="eyebrow">All activity ({(data?.activity || []).length})</p>
            <p className="text-xs text-[var(--adn-text-muted)] mt-1">Every action you've taken in the marketplace.</p>
          </div>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--adn-cream)] text-left text-[10px] uppercase tracking-[0.12em] text-[var(--adn-text-muted)] sticky top-0">
                <tr><th className="px-3 py-2.5">When</th><th className="px-3 py-2.5">Action</th><th className="px-3 py-2.5">IP</th></tr>
              </thead>
              <tbody className="divide-y divide-[var(--adn-line)]">
                {(data?.activity || []).map((a) => (
                  <tr key={a.log_id}>
                    <td className="px-3 py-2 text-[var(--adn-text-soft)] text-xs whitespace-nowrap">{fmtDate(a.at)}</td>
                    <td className="px-3 py-2 text-xs font-mono whitespace-nowrap">{a.action}</td>
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{a.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function ChangePasswordSection() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      setCur(""); setNext("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setBusy(false); }
  };
  return (
    <section className="mt-10 max-w-3xl">
      <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><Lock size={18} className="text-[var(--adn-gold)]" /> Change password</h2>
      <form onSubmit={submit} className="bg-white border border-[var(--adn-line)] p-5 space-y-3">
        <label className="block">
          <span className="text-xs font-semibold text-[var(--adn-text-soft)] mb-1.5 block uppercase tracking-wider">Current password</span>
          <input data-testid="cur-pw" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="p-input" autoComplete="current-password" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-[var(--adn-text-soft)] mb-1.5 block uppercase tracking-wider">New password (min 8)</span>
          <input data-testid="new-pw" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="p-input" autoComplete="new-password" />
        </label>
        <button data-testid="submit-pw" type="submit" disabled={busy || !next} className="btn-outline-navy px-5 py-2.5 rounded text-sm">
          {busy ? "Saving…" : "Update password"}
        </button>
        <p className="text-[11px] text-[var(--adn-text-muted)]">If you signed up with Google, leave current password blank to set one for the first time.</p>
      </form>
    </section>
  );
}

function Mini({ k, v, mono, full, small }) {
  return (
    <div className={`border border-[var(--adn-line)] p-3 ${full ? "col-span-full" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--adn-text-muted)] font-bold">{k}</div>
      <div className={`mt-1 ${mono ? "font-mono" : ""} ${small ? "text-xs break-all" : "text-sm"}`}>{v}</div>
    </div>
  );
}
